import React, { useContext, useState } from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useHistory, useLocation } from 'react-router-dom';
import Button from '@cpg-its/ads.common.button';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonWrapper } from './layout/button-wrapper';
import { TcsmModal } from './tcsm-modal';
import { TcsmStates } from '../../root/tcsm-model';
import { buttonStyles } from '../constants/theme/styles/components/buttonstyles';
import { TcsmSiteSettingsProps } from '../models/epi-model';
import { ExternalLinkState } from '../models/app-context-model';
import { setExternalLinkState } from '../ducks/app-context';
import { DemographicAppKeys, DemographicsContext } from '../models/demographics-model';
import { AuthenticatedState } from '../models/authenticated-model';
import { EmploymentState } from '../../pages/arc/features/employment/employment-model';
import { useBasePath } from '../hooks/useBasePath';
import { ArcRoutes } from '../../pages/arc/constants/routes';
import { handleExternalLinkClick } from '../helpers/url-helper';
import { WysiwygWithReactLink } from './wysiwyg-with-react-link';
import { Navigation } from '../models/navigation-model';
import { useRoleObject } from '../../pages/arc/components/useRoles';
import { CicElectronicAgreementModal } from '../../cic/cic-electronic-agreement-modal';
import { OptOutModal } from '../../cic/opt-out-modal';

interface ModalProps {
	navigation?: Navigation;
}

const electronicDeliveryLinkTag = 'electronicdeliverycicuser';

export function ExternalLinkModal(props: ModalProps) {
	const dispatch = useDispatch();
	const { navigation } = props;

	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const buttonClasses = buttonStyles();
	const externalLinkState = useSelector<TcsmStates, ExternalLinkState>(state => state.appContext?.externalLinkState);
	const [clientId, setClientId] = useState('0');
	const [optInClientId, setoptInClientId] = useState('0');

	const roles = useRoleObject();
	const handleClose = () => {
		dispatch(setExternalLinkState({
			renderModal: false,
			linkUrl: '',
			linkInnerHtml: '',
		}));
		setClientId('0');
		setoptInClientId('0');
	};

	const isRemoteSupportLink = (url: string) => {
		return url?.includes('cpg.support.me');
	};

	const cicReplacement = (externalLinkState?.linkUrl?.includes(navigation?.CicLinkUrl) && roles?.customer360?.length === 1) && navigation?.CicLinkUrl?.replace('{clientId}', roles?.customer360[0]?.role_target);
	const optIn = roles?.customer360?.length === 1 ? roles?.customer360[0]?.opted_in : roles?.customer360?.find(a => a?.role_target === clientId)?.opted_in;
	const moreThanOneCicAndPickedOrg = (roles?.customer360?.length > 1 && optInClientId !== '0') || roles?.customer360?.length === 1;
	const isCicLink = externalLinkState?.linkUrl?.includes(navigation?.CicLinkUrl);
	const isElectronicDeliveryLink = externalLinkState?.linkUrl?.includes(electronicDeliveryLinkTag);
	const continueToOptInFlow = () => {
		setoptInClientId(clientId);
	};
	function CiaSelect() {
		return (
			<>
				<WysiwygWithReactLink content={navigation?.ModalContent} />
				<InputLabel id='label'>{navigation?.OrganizationLabel}</InputLabel>
				<Select labelId='label' id='select' value={clientId} onChange={(evt: any) => setClientId(evt?.target?.value)} >
					<MenuItem key='0' value='0'>- Please Select -</MenuItem>
					{
						roles?.customer360?.map(org => <MenuItem key={org?.role_target} value={org?.role_target} >{org?.institution_name}</MenuItem>)
					}
				</Select>
				<ButtonWrapper flexEnd>
					<button type='button' className={buttonClasses.tertiaryButton} onClick={handleClose} >{navigation?.CancelButtonText}</button>
					{
						(optIn && !isElectronicDeliveryLink) ?
							<a href={navigation?.CicLinkUrl?.replace('{clientId}', clientId)} onClick={handleClose} className={`${buttonClasses.primaryButton} ${clientId === '0' && 'disabled'}`} target='_blank' rel='noreferrer'>{navigation?.ContinueButtonText}</a> :
							<button className={`${buttonClasses.primaryButton} ${clientId === '0' && 'disabled'}`} type='button' onClick={continueToOptInFlow} >{navigation?.ContinueButtonText}</button>
					}
				</ButtonWrapper>
			</>
		);
	}

	if (roles?.customer360?.length > 0 && !optIn && moreThanOneCicAndPickedOrg && (isCicLink || isElectronicDeliveryLink)) {
		return <CicElectronicAgreementModal handleClose={handleClose} clientId={roles?.customer360?.length === 1 ? roles?.customer360[0]?.role_target : clientId} />;
	}

	if (roles?.customer360?.length > 0 && optIn && moreThanOneCicAndPickedOrg && isElectronicDeliveryLink) {
		return <OptOutModal handleClose={handleClose} />;
	}


	return (
		<TcsmModal
			open={externalLinkState?.renderModal}
			handleClose={handleClose}
		>
			{
				(isCicLink || isElectronicDeliveryLink) && roles?.customer360?.length > 1 ?
					CiaSelect() :
					<>
						<p>{isRemoteSupportLink(externalLinkState?.linkUrl) ? siteSettings?.remoteSupportLinkModalMessage : siteSettings?.externalLinkModalMessage || 'You are leaving this site and will be redirect to another website.'}</p>
						<ButtonWrapper flexEnd>
							<Button type='button' buttonStyle='tertiary' text={siteSettings?.externalLinkModalCancelButtonText || 'Cancel'} backgroundColor='#808080' onClick={handleClose} />
							<a
								href={cicReplacement || externalLinkState?.linkUrl}
								className={buttonClasses.primaryButton}
								target='_blank' rel='noreferrer'
								onClick={handleClose}>{siteSettings?.externalLinkModalContinueButtonText || 'Continue'}</a>
						</ButtonWrapper>
					</>
			}
		</TcsmModal>
	);
}

ExternalLinkModal.defaultProps = {
	navigation: null,
};

interface Props {
	linkText: string | JSX.Element;
	linkUrl: string;
	className?: string;
	linkIcon?: JSX.Element;
	newWindow?: boolean;
	iconRight?: boolean;
	onClickHandler?: () => void;
	forceReloadTcsm?: boolean;
}
export function ExternalLink(props: Props) {
	const { linkText, linkUrl, className, linkIcon, newWindow, iconRight, forceReloadTcsm } = props;

	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const app = useContext(DemographicsContext);
	const authenticatedState = useSelector<TcsmStates, AuthenticatedState>(state => state.authenticatedState);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const employeeSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.EmployeeSearch}`);
	const buttonClasses = buttonStyles();


	const handleLinkClick = (e) => {
		if (props?.onClickHandler) props.onClickHandler();
		handleExternalLinkClick(e, dispatch, history, location, authenticatedState, employmentState, app, employeeSelect, null, forceReloadTcsm);
	};
	if (iconRight) {
		return (
			<>
				<a
					target={newWindow ? '_blank' : ''}
					rel="noreferrer"
					href={linkUrl}
					className={`${className} ${linkIcon && buttonClasses.linkButton}`}
					onClick={handleLinkClick}><span>{linkText}</span>{linkIcon}</a>
			</>
		);
	}
	return (
		<>
			<a
				target={newWindow ? '_blank' : ''}
				rel="noreferrer"
				href={linkUrl}
				className={`${className} ${linkIcon && buttonClasses.linkButton}`}
				onClick={handleLinkClick}>{linkIcon}<span>{linkText}</span></a>

		</>
	);
}


interface SearchExternalLinkProps {
	linkText: string | JSX.Element;
	linkUrl: string;
	history: any;
	location: any;
	dispatch: any;
	app: DemographicAppKeys;
	authenticatedState: any;
	employmentState: any;
	employeeSelect: string;
	className?: string;
}
export function SearchExternalLink(props: SearchExternalLinkProps) {
	const { linkText, linkUrl, className, history, location, dispatch, app, authenticatedState, employmentState, employeeSelect } = props;

	const handleLinkClick = (e) => {
		handleExternalLinkClick(e, dispatch, history, location, authenticatedState, employmentState, app, employeeSelect);
	};

	return (
		<>
			<a
				href={linkUrl}
				className={className}
				onClick={handleLinkClick}>{linkText}</a>

		</>
	);
}


ExternalLink.defaultProps = {
	className: '',
	linkIcon: '',
	newWindow: false,
	iconRight: false,
	onClickHandler: null,
	forceReloadTcsm: false,
};

SearchExternalLink.defaultProps = {
	className: '',
};